import { useState } from 'react';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { AdminMicrostories } from '@platform/client';
import { AdminMicrostory } from '@platform/client/schemas';
import { MicrostoryForm, MicrostoryFormData } from './MicrostoryForm';

type UpdateMicrostoryModalProps = {
  microstory: AdminMicrostory;
  onClose: () => void;
};

export const UpdateMicrostoryModal = (props: UpdateMicrostoryModalProps) => {
  const { microstory } = props;
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { ...functions } = useForm<MicrostoryFormData>({
    defaultValues: {
      contactName: microstory.contactName,
      organizationName: microstory.organizationName,
      microstory: microstory.microstory,
      userType: microstory.userType,
      source: microstory.source,
      storyDate: new Date(microstory.storyDate),
      urlOrFileRef: microstory.urlOrFileRef,
      consentToShare: microstory.consentToShare,
      corporateClientId:
        microstory.corporateClientId != null ? String(microstory.corporateClientId) : null,
      impactPartnerId:
        microstory.impactPartnerId != null ? String(microstory.impactPartnerId) : null,
    },
  });
  const queryClient = useQueryClient();
  const updateMicrostoryMutator = AdminMicrostories.useUpdateMicrostory({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => setModalStage('error'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminMicrostories.getGetAdminMicrostoriesQueryKey(),
        });
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<MicrostoryFormData> = (data) => {
    updateMicrostoryMutator.mutate({
      microstoryId: microstory.id,
      data: {
        contactName: data.contactName,
        organizationName: data.organizationName,
        microstory: data.microstory,
        userType: data.userType,
        source: data.source,
        storyDate: data.storyDate.toISOString(),
        urlOrFileRef: data.urlOrFileRef || null,
        consentToShare: data.consentToShare,
        corporateClientId: data.corporateClientId ? Number(data.corporateClientId) : null,
        impactPartnerId: data.impactPartnerId ? Number(data.impactPartnerId) : null,
      },
    });
  };

  return (
    <AdminFormModal
      variant='full-screen'
      title='Update Microstory'
      modalStage={modalStage}
      onClose={props.onClose}
      submitCTA={{ text: 'Save', callback: () => functions.handleSubmit(onSubmit)() }}
    >
      <MicrostoryForm {...functions} />
    </AdminFormModal>
  );
};
